/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { debounce } from '@theme/utilities';

/**
 * Sizebay CDN prescript always clicks Add to cart after 800ms, even when the
 * recommended size is sold out or cannot be matched. Patch Implantation so we
 * only add to cart when the recommended size is available.
 */
const SIZE_FIELDSET_SELECTOR = 'fieldset.variant-option--grid[data-sizebay-pending]';
const OBSERVER_ROOT_SELECTOR = 'variant-picker:has(#sizebay-container), .product-details:has(#sizebay-container)';
const ADD_TO_CART_DELAY_MS = 800;
const SYNC_LAYOUT_DEBOUNCE_MS = 50;
const SYNC_LAYOUT_BOOT_DEBOUNCE_MS = 120;
const SYNC_SELECTION_DEBOUNCE_MS = 50;
const SIZEBAY_PICKER_REVEAL_TIMEOUT_MS = 5000;
const SIZEBAY_NO_RECOMMENDATION_REVEAL_MS = 2500;
const SIZEBAY_LABEL_FADE_MS = 350;
const SIZEBAY_IMPLANTATION_PATCH_TIMEOUT_MS = 10000;

/** @type {WeakMap<HTMLElement, string>} */
const lastRecommendationTextBySlot = new WeakMap();

/** @type {WeakMap<HTMLElement, string>} */
const lastRecommendedSizeByFieldset = new WeakMap();

/** @param {Node | null | undefined} target @returns {Element | null} */
const mutationTargetElement = (target) => {
  if (target instanceof Element) return target;
  // CharacterData (Text/Comment) and other non-Element nodes from MutationObserver.
  if (target instanceof Node) return target.parentElement;
  return null;
};

/** @param {Node | null | undefined} target @returns {boolean} */
const isSizebayRecommendationMutationTarget = (target) => {
  const element = mutationTargetElement(target);
  if (!(element instanceof Element)) return false;

  return (
    element.id === 'szb-vfr-recommendation' ||
    element.closest('#szb-vfr-recommendation') !== null ||
    Boolean(element.id?.startsWith('szb-look-')) ||
    element.closest('[id^="szb-look-"]') !== null
  );
};

/** @type {Set<string>} */
const revealedFieldsetKeys = new Set();

/** @type {Set<HTMLElement>} */
const observedSlots = new Set();

/** @type {Map<HTMLElement, MutationObserver>} */
const slotObservers = new Map();

/** @type {Map<HTMLElement, ReturnType<typeof debounce>>} */
const slotUpdateDebouncers = new Map();

/** @type {HTMLElement[] | null} */
let cachedAllSizeFieldsets = null;

/** @type {HTMLElement[] | null} */
let cachedPendingFieldsets = null;

/** @type {boolean} */
let isSizebayLoaded = false;

/** @type {number | null} */
let revealPickerTimeoutId = null;

/** @type {boolean} */
let isApplyingSizebaySelection = false;

/** @type {boolean} */
let userSizeSelectionListenerBound = false;

/** @param {HTMLElement} fieldset */
const clearAwaitingSelection = (fieldset) => {
  if (!(fieldset instanceof HTMLElement)) return;
  fieldset.removeAttribute('data-sizebay-awaiting-selection');
};

/** @param {HTMLElement} fieldset */
const markAwaitingSelection = (fieldset) => {
  if (!(fieldset instanceof HTMLElement) || !isFieldsetPending(fieldset)) return;
  fieldset.setAttribute('data-sizebay-awaiting-selection', '');
};

/**
 * Lets a shopper's manual size choice show immediately, while Sizebay-driven
 * clicks keep the selection visually hidden until reveal.
 * @param {Event} event
 */
const handleUserSizeSelection = (event) => {
  if (isApplyingSizebaySelection) return;

  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

  const fieldset = target.closest(SIZE_FIELDSET_SELECTOR);
  if (!(fieldset instanceof HTMLElement) || !fieldset.hasAttribute('data-sizebay-awaiting-selection')) return;

  updateLegend(target);
  clearAwaitingSelection(fieldset);
};

const bindUserSizeSelectionListener = () => {
  if (userSizeSelectionListenerBound) return;
  userSizeSelectionListenerBound = true;
  document.addEventListener('change', handleUserSizeSelection, true);
};

/** @type {boolean} */
let hasCompletedInitialSizebaySync = false;

/** @type {MutationObserver | null} */
let layoutObserver = null;

/** @type {boolean} */
let isSyncingLayout = false;

/** @type {boolean} */
let suppressSlotObserverSync = false;

/** @type {ReturnType<typeof debounce> | null} */
let debouncedTryRevealAfterPresentation = null;

/** @type {boolean} */
let pendingSyncLayoutRequest = false;

/** @type {boolean} */
let pendingRevealAfterVariantUpdate = false;

/** @type {boolean} */
let hasGivenUpOnSizebayRecommendation = false;

/** @type {number | null} */
let noRecommendationRevealTimeoutId = null;

/** @type {number | null} */
let syncLayoutTimeoutId = null;

/** @type {number | null} */
let implantationPatchTimeoutId = null;

/** @type {Map<HTMLElement, ReturnType<typeof debounce>>} */
const slotClearDebouncers = new Map();

const invalidateFieldsetCache = () => {
  cachedAllSizeFieldsets = null;
  cachedPendingFieldsets = null;
};

const refreshFieldsetCache = () => {
  cachedAllSizeFieldsets = Array.from(document.querySelectorAll(SIZE_FIELDSET_SELECTOR)).filter(
    (fieldset) => fieldset instanceof HTMLElement,
  );
  cachedPendingFieldsets = cachedAllSizeFieldsets;
};

/** @returns {HTMLElement[]} */
const getAllSizeFieldsets = () => {
  if (!cachedAllSizeFieldsets) refreshFieldsetCache();
  return cachedAllSizeFieldsets ?? [];
};

/** @returns {HTMLElement[]} */
const getSizeFieldsets = () => {
  if (!cachedPendingFieldsets) refreshFieldsetCache();
  return cachedPendingFieldsets ?? [];
};

/** @returns {HTMLElement | null} */
const getSizeFieldset = () => getSizeFieldsets()[0] ?? null;

/** @param {HTMLElement} slot @returns {ReturnType<typeof debounce>} */
const getDebouncedClearSlot = (slot) => {
  let debounced = slotClearDebouncers.get(slot);

  if (!debounced) {
    debounced = debounce((target) => {
      if (!(target instanceof HTMLElement)) return;
      if (!target.querySelector('#szb-vfr-recommendation')) {
        clearSlot(target);
      }
    }, 400);
    slotClearDebouncers.set(slot, debounced);
  }

  return debounced;
};

/** @param {HTMLElement} [slot] */
const cancelPendingClearSlot = (slot) => {
  if (slot instanceof HTMLElement) {
    slotClearDebouncers.get(slot)?.cancel();
    return;
  }

  for (const debounced of slotClearDebouncers.values()) {
    debounced.cancel();
  }
};

/** @param {HTMLElement} fieldset @returns {string} */
const getFieldsetKey = (fieldset) => {
  if (!(fieldset instanceof HTMLElement)) return '';
  return `${fieldset.dataset.fieldsetIndex ?? ''}:${fieldset.dataset.currentRegion ?? ''}`;
};

/** @param {HTMLElement} fieldset @returns {boolean} */
const isFieldsetRevealed = (fieldset) => {
  if (!(fieldset instanceof HTMLElement)) return false;
  if (fieldset.hasAttribute('data-sizebay-ready')) return true;
  return revealedFieldsetKeys.has(getFieldsetKey(fieldset));
};

/** @param {HTMLElement | null | undefined} fieldset @returns {boolean} */
const isFieldsetPending = (fieldset) =>
  fieldset instanceof HTMLElement && fieldset.hasAttribute('data-sizebay-pending') && !isFieldsetRevealed(fieldset);

/** @returns {boolean} */
const isSizePickerPending = () => getSizeFieldsets().some(isFieldsetPending);

/** @returns {boolean} */
const areAllSizeFieldsetsRevealed = () => {
  const fieldsets = getSizeFieldsets();
  if (fieldsets.length === 0) return true;

  for (const fieldset of fieldsets) {
    if (!isFieldsetRevealed(fieldset)) return false;
  }

  return true;
};

/** @param {HTMLElement} fieldset @returns {HTMLElement | null} */
const getFieldsetLook = (fieldset) => {
  const look = fieldset.previousElementSibling;
  return look instanceof HTMLElement && look.id?.startsWith('szb-look-') ? look : null;
};

/** @param {HTMLElement} recommendation @returns {HTMLElement | null} */
const getRecommendationFieldset = (recommendation) => {
  const slot = recommendation.closest('[data-sizebay-recommendation-slot]');
  const fieldset = slot?.closest('fieldset.variant-option--grid');
  return fieldset instanceof HTMLElement ? fieldset : null;
};

/** @param {HTMLElement} el */
const hasRecommendationContent = (el) => {
  const text = el.textContent?.replace(/\u00a0/g, ' ').trim();
  return Boolean(text);
};

/** @param {HTMLElement} el @returns {boolean} */
const hasActionableRecommendation = (el) => {
  return hasRecommendationContent(el) && Boolean(parseRecommendedSize(el));
};

/** @param {HTMLElement} fieldset @returns {boolean} */
const fieldsetHasRecommendation = (fieldset) => {
  const slot = fieldset.querySelector('[data-sizebay-recommendation-slot]');
  const recommendationInSlot = slot?.querySelector('#szb-vfr-recommendation');
  return recommendationInSlot instanceof HTMLElement && hasActionableRecommendation(recommendationInSlot);
};

/** @param {HTMLElement} fieldset @returns {boolean} */
const fieldsetSizebayStillInitializing = (fieldset) => {
  if (!fieldset.querySelector('#sizebay-container')) return false;
  if (getFieldsetLook(fieldset)) return false;

  const looks = document.querySelectorAll('[id^="szb-look-"]');
  return looks.length < getAllSizeFieldsets().length;
};

/** @param {HTMLElement} fieldset @returns {boolean} */
const fieldsetAwaitingRecommendation = (fieldset) => {
  if (!fieldset.querySelector('#sizebay-container')) return false;

  const look = getFieldsetLook(fieldset);
  if (look instanceof HTMLElement) {
    const recInLook = look.querySelector('#szb-vfr-recommendation');

    if (recInLook instanceof HTMLElement && hasActionableRecommendation(recInLook)) return true;
    if (fieldsetHasRecommendation(fieldset)) return true;

    // Look is mounted; Sizebay may still inject/fill the recommendation.
    // Only stop waiting after the no-recommendation timeout gives up.
    return !hasGivenUpOnSizebayRecommendation;
  }

  return fieldsetSizebayStillInitializing(fieldset);
};

/** @param {HTMLElement} fieldset @returns {boolean} */
const canRevealFieldsetWithoutRecommendation = (fieldset) => {
  if (fieldsetHasRecommendation(fieldset)) return false;
  if (fieldsetAwaitingRecommendation(fieldset)) return false;
  if (fieldsetSizebayStillInitializing(fieldset)) return false;
  return true;
};

/** @returns {boolean} */
const hasPendingRecommendationInAnyLook = () => {
  for (const fieldset of getAllSizeFieldsets()) {
    const pendingInLook = getFieldsetLook(fieldset)?.querySelector('#szb-vfr-recommendation');
    if (pendingInLook instanceof HTMLElement && hasActionableRecommendation(pendingInLook)) {
      return true;
    }
  }

  return false;
};

/** @param {HTMLElement | null | undefined} fieldset @returns {boolean} */
const isSizebayPickerPresentationReady = (fieldset) => {
  if (!(fieldset instanceof HTMLElement)) return false;

  const slot = fieldset.querySelector('[data-sizebay-recommendation-slot]');
  const recommendationInSlot = slot?.querySelector('#szb-vfr-recommendation');

  if (!(recommendationInSlot instanceof HTMLElement && hasActionableRecommendation(recommendationInSlot))) {
    return false;
  }

  const size = parseRecommendedSize(recommendationInSlot);
  if (!size) return false;

  const input = findSizeInput(size, fieldset);
  // Slot is populated on reveal — do not require is-populated here.
  return input instanceof HTMLInputElement && input.checked && slot instanceof HTMLElement;
};

/** @returns {boolean} */
const hasPendingFieldsetsWaitingForRecommendation = () => {
  if (hasGivenUpOnSizebayRecommendation) return false;

  for (const fieldset of getSizeFieldsets()) {
    if (!isFieldsetPending(fieldset)) continue;
    if (!fieldset.querySelector('#sizebay-container')) continue;
    if (fieldsetHasRecommendation(fieldset)) continue;
    return true;
  }

  return false;
};

/** @returns {boolean} */
const hasPendingFieldsetsWithoutRecommendation = () => {
  for (const fieldset of getSizeFieldsets()) {
    if (!isFieldsetPending(fieldset)) continue;
    if (canRevealFieldsetWithoutRecommendation(fieldset)) return true;
  }

  return false;
};

const clearRevealPickerTimeout = () => {
  if (revealPickerTimeoutId != null) {
    window.clearTimeout(revealPickerTimeoutId);
    revealPickerTimeoutId = null;
  }
};

const cancelNoRecommendationReveal = () => {
  if (noRecommendationRevealTimeoutId != null) {
    window.clearTimeout(noRecommendationRevealTimeoutId);
    noRecommendationRevealTimeoutId = null;
  }
};

const scheduleNoRecommendationReveal = () => {
  if (noRecommendationRevealTimeoutId != null) return;
  if (!isSizePickerPending()) return;
  if (hasPendingRecommendationInAnyLook()) return;
  if (!hasPendingFieldsetsWaitingForRecommendation()) return;

  noRecommendationRevealTimeoutId = window.setTimeout(() => {
    noRecommendationRevealTimeoutId = null;
    hasGivenUpOnSizebayRecommendation = true;
    processFieldsetReveals('fallback');
  }, SIZEBAY_NO_RECOMMENDATION_REVEAL_MS);
};

/** @returns {ReturnType<typeof debounce>} */
const getDebouncedTryRevealAfterPresentation = () => {
  if (!debouncedTryRevealAfterPresentation) {
    debouncedTryRevealAfterPresentation = debounce(() => {
      processFieldsetReveals('recommended');
    }, SYNC_SELECTION_DEBOUNCE_MS);
  }

  return debouncedTryRevealAfterPresentation;
};

const teardownObserversIfComplete = () => {
  if (!areAllSizeFieldsetsRevealed()) return;

  // Keep slot observers alive for Find my size updates after reveal.
  // Layout observer stays connected but ignores non-Sizebay picker mutations.
};

const finalizeRevealPhase = () => {
  if (!isSizePickerPending()) {
    clearRevealPickerTimeout();
    cancelNoRecommendationReveal();
    getDebouncedTryRevealAfterPresentation().cancel();
    teardownObserversIfComplete();
  }
};

/** @returns {boolean} */
const ensureSizebaySyncReady = () => {
  if (!isSizebayRuntimeReady()) return false;

  if (!isSizebayLoaded) {
    isSizebayLoaded = true;
    patchImplantation();
  }

  hasCompletedInitialSizebaySync = true;
  return true;
};

const forceRevealPendingFieldsets = () => {
  pendingRevealAfterVariantUpdate = false;
  hasGivenUpOnSizebayRecommendation = true;

  for (const fieldset of getSizeFieldsets()) {
    if (!isFieldsetPending(fieldset)) continue;

    if (fieldsetHasRecommendation(fieldset)) {
      const recommendation = fieldset.querySelector('#szb-vfr-recommendation');
      if (recommendation instanceof HTMLElement) {
        syncSelection(recommendation);
        if (pendingRevealAfterVariantUpdate) continue;
      }
    } else if (selectFirstAvailableSize(fieldset)) {
      continue;
    }

    revealSizePicker(fieldset);
  }

  finalizeRevealPhase();
};

/**
 * @param {'recommended' | 'fallback'} strategy
 */
const processFieldsetReveals = (strategy) => {
  if (strategy === 'recommended' && pendingRevealAfterVariantUpdate) return;
  if (!ensureSizebaySyncReady()) return;
  if (strategy === 'fallback' && hasPendingRecommendationInAnyLook()) return;

  for (const fieldset of getSizeFieldsets()) {
    if (!isFieldsetPending(fieldset)) continue;

    if (strategy === 'recommended') {
      if (!fieldsetHasRecommendation(fieldset)) continue;
      if (!isSizebayPickerPresentationReady(fieldset)) continue;
    } else {
      if (!canRevealFieldsetWithoutRecommendation(fieldset)) continue;
      if (selectFirstAvailableSize(fieldset)) continue;
    }

    revealSizePicker(fieldset);
  }

  finalizeRevealPhase();
};

const tryRevealSizePicker = () => processFieldsetReveals('recommended');

const revealFieldsetsWithoutRecommendation = () => processFieldsetReveals('fallback');

const scheduleRevealPickerFallback = () => {
  if (!isSizePickerPending()) return;

  clearRevealPickerTimeout();
  revealPickerTimeoutId = window.setTimeout(() => {
    ensureSizebaySyncReady();
    hasCompletedInitialSizebaySync = true;

    tryRevealSizePicker();
    revealFieldsetsWithoutRecommendation();

    if (isSizePickerPending()) {
      forceRevealPendingFieldsets();
    }
  }, SIZEBAY_PICKER_REVEAL_TIMEOUT_MS);
};

/** @param {HTMLElement} slot */
const disconnectSlotObserver = (slot) => {
  if (!(slot instanceof HTMLElement)) return;

  slotObservers.get(slot)?.disconnect();
  slotObservers.delete(slot);
  slotUpdateDebouncers.get(slot)?.cancel();
  slotUpdateDebouncers.delete(slot);
  slotClearDebouncers.get(slot)?.cancel();
  slotClearDebouncers.delete(slot);
  slot.removeAttribute('data-sizebay-slot-observed');
  observedSlots.delete(slot);
};

const disconnectAllSlotObservers = () => {
  for (const slot of observedSlots) {
    disconnectSlotObserver(slot);
  }
};

/** @param {HTMLElement} fieldset */
const revealSizePicker = (fieldset) => {
  if (!isFieldsetPending(fieldset)) return;

  const key = getFieldsetKey(fieldset);
  if (key) {
    revealedFieldsetKeys.add(key);
  }

  fieldset.setAttribute('data-sizebay-ready', '');

  const slot = fieldset.querySelector('[data-sizebay-recommendation-slot]');
  const recommendation = fieldset.querySelector('#szb-vfr-recommendation');
  const hasRecommendation = recommendation instanceof HTMLElement && hasRecommendationContent(recommendation);

  // Single visual commit: legend text → recommendation slot → selected grid style.
  let legendInput = null;

  if (hasRecommendation) {
    const parsedSize = parseRecommendedSize(recommendation);
    if (parsedSize) {
      lastRecommendedSizeByFieldset.set(fieldset, parsedSize);

      const recommendedInput = findSizeInput(parsedSize, fieldset);
      if (recommendedInput instanceof HTMLInputElement && recommendedInput.checked) {
        legendInput = recommendedInput;
      }
    }
  }

  if (!legendInput) {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) {
      legendInput = checked;
    }
  }

  if (legendInput) {
    updateLegend(legendInput);
  }

  // Clear awaiting before mounting so legend, selected grid style and
  // recommendation label commit in the same frame.
  clearAwaitingSelection(fieldset);

  if (slot instanceof HTMLElement && hasRecommendation) {
    mountSlot(slot);
    slot.classList.remove('is-settled');

    window.setTimeout(() => {
      settleSlot(slot);
    }, SIZEBAY_LABEL_FADE_MS);
  } else if (slot instanceof HTMLElement) {
    settleSlot(slot);
  }
};

/** @param {() => void} callback */
const afterVariantPickerSettles = (callback) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

/** @returns {boolean} */
const isSizebayRuntimeReady = () =>
  isSizebayLoaded ||
  Boolean(/** @type {any} */ (window).Sizebay?.Implantation) ||
  Boolean(document.querySelector('[id^="szb-look-"]'));

/** Ensures module state reflects an already-booted Sizebay prescript (soft nav). */
const bootstrapSizebayIfReady = () => {
  if (!isSizebayRuntimeReady()) return false;

  isSizebayLoaded = true;
  patchImplantation();
  return true;
};

/** Resets picker state for a new product without clearing the global Sizebay boot flag. */
const resetSizePickerForNewProduct = () => {
  hasCompletedInitialSizebaySync = false;
  hasGivenUpOnSizebayRecommendation = false;
  pendingRevealAfterVariantUpdate = false;
  pendingSyncLayoutRequest = false;
  revealedFieldsetKeys.clear();
  clearRevealPickerTimeout();
  cancelNoRecommendationReveal();
  getDebouncedTryRevealAfterPresentation().cancel();
  disconnectAllSlotObservers();
  invalidateFieldsetCache();
  refreshFieldsetCache();

  for (const fieldset of getSizeFieldsets()) {
    fieldset.removeAttribute('data-sizebay-ready');
    markAwaitingSelection(fieldset);
  }

  scheduleRevealPickerFallback();
};

const teardownSizePickerIntegration = () => {
  hasCompletedInitialSizebaySync = false;
  hasGivenUpOnSizebayRecommendation = false;
  pendingRevealAfterVariantUpdate = false;
  pendingSyncLayoutRequest = false;
  suppressSlotObserverSync = false;
  revealedFieldsetKeys.clear();
  clearRevealPickerTimeout();
  cancelNoRecommendationReveal();
  getDebouncedTryRevealAfterPresentation().cancel();
  scheduleSyncLayout.cancel();
  clearImplantationPatchTimeout();
  disconnectAllSlotObservers();
  disconnectLayoutObserver();
  cancelPendingClearSlot();
  slotClearDebouncers.clear();
  invalidateFieldsetCache();
};

/** Re-runs Sizebay picker sync after soft navigation or section HTML swap. */
const reinitializeSizePickerForSection = () => {
  resetSizePickerForNewProduct();
  cancelPendingClearSlot();
  bootstrapSizebayIfReady();
  scheduleSyncLayout.cancel();
  connectLayoutObserver();
  syncLayout();
};

/** @returns {HTMLElement | null} */
const getSlotInner = (slot) => {
  if (!(slot instanceof HTMLElement)) return null;
  const inner = slot.querySelector('.variant-option__sizebay-slot-inner');
  return inner instanceof HTMLElement ? inner : slot;
};

/** @param {HTMLElement} recommendation @param {HTMLElement} slot */
const syncRecommendationPresentation = (recommendation, slot) => {
  const normalizedText = recommendation.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';

  if (!hasRecommendationContent(recommendation)) {
    cancelPendingClearSlot(slot);
    lastRecommendationTextBySlot.delete(slot);

    if (slot.classList.contains('is-populated')) {
      clearSlot(slot);
    }

    return;
  }

  cancelPendingClearSlot(slot);

  const fieldset = slot.closest('fieldset.variant-option--grid');
  const isPending = fieldset instanceof HTMLElement && isFieldsetPending(fieldset);

  // While pending, keep the recommendation in the DOM but do not expand the
  // slot — visual commit (label + grid + legend) happens once in revealSizePicker.
  if (isPending) {
    lastRecommendationTextBySlot.set(slot, normalizedText);
    return;
  }

  const previousText = lastRecommendationTextBySlot.get(slot);
  const recommendationChanged = previousText != null && previousText !== normalizedText;

  mountSlot(slot);

  if (recommendationChanged && slot.classList.contains('is-settled')) {
    slot.classList.remove('is-settled');

    window.setTimeout(() => {
      settleSlot(slot);
    }, SIZEBAY_LABEL_FADE_MS);
  }

  lastRecommendationTextBySlot.set(slot, normalizedText);
};

/** @param {HTMLElement} slot */
const settleSlot = (slot) => {
  slot.classList.add('is-settled');
};

/** @param {HTMLElement} slot @returns {boolean} */
const shouldDeferSlotSettle = (slot) => {
  const fieldset = slot.closest('fieldset.variant-option--grid');
  return fieldset instanceof HTMLElement && isFieldsetPending(fieldset);
};

/** @param {HTMLElement} slot */
const maybeSettleSlot = (slot) => {
  if (shouldDeferSlotSettle(slot)) return;
  settleSlot(slot);
};

/** @param {HTMLElement} slot */
const mountSlot = (slot) => {
  cancelPendingClearSlot();

  const wasPopulated = slot.classList.contains('is-populated');

  slot.classList.add('is-populated');
  slot.setAttribute('data-sizebay-populated', '');

  if (wasPopulated) {
    maybeSettleSlot(slot);
    return;
  }

  slot.classList.remove('is-settled');

  const onTransitionEnd = (event) => {
    if (event.target !== slot) return;
    maybeSettleSlot(slot);
    slot.removeEventListener('transitionend', onTransitionEnd);
  };

  slot.addEventListener('transitionend', onTransitionEnd);
  window.setTimeout(() => {
    if (!slot.classList.contains('is-settled')) {
      maybeSettleSlot(slot);
      slot.removeEventListener('transitionend', onTransitionEnd);
    }
  }, 300);
};

/** @param {HTMLElement} slot */
const clearSlot = (slot) => {
  slot.classList.remove('is-populated', 'is-settled');
  slot.removeAttribute('data-sizebay-populated');
};

/** @param {HTMLElement} slot @param {{ applySelection?: boolean }} [options] */
const syncSlotContent = (slot, options = {}) => {
  if (!(slot instanceof HTMLElement)) return;

  const recommendation = slot.querySelector('#szb-vfr-recommendation');

  if (recommendation instanceof HTMLElement) {
    const fieldset = slot.closest('fieldset.variant-option--grid');
    const size = parseRecommendedSize(recommendation);
    const cachedSize = fieldset instanceof HTMLElement ? lastRecommendedSizeByFieldset.get(fieldset) : undefined;
    const normalizedText = recommendation.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';
    const previousText = lastRecommendationTextBySlot.get(slot);
    const recommendationTextChanged = previousText != null && previousText !== normalizedText;
    const sizeChanged = size != null && size !== cachedSize;
    const shouldApplySelection =
      options.applySelection ??
      (fieldset instanceof HTMLElement && (isFieldsetPending(fieldset) || sizeChanged || recommendationTextChanged));

    syncRecommendationPresentation(recommendation, slot);

    if (shouldApplySelection) {
      syncSelection(recommendation);
    }

    return;
  }

  if (slot.classList.contains('is-populated')) {
    getDebouncedClearSlot(slot)(slot);
  }
};

/**
 * @param {HTMLElement} slot
 * @param {{ forceSync?: boolean, applySelection?: boolean }} [options]
 */
const observeSlot = (slot, options = {}) => {
  if (!(slot instanceof HTMLElement)) return;

  let debouncedUpdate = slotUpdateDebouncers.get(slot);

  if (!debouncedUpdate) {
    debouncedUpdate = debounce(() => {
      if (suppressSlotObserverSync) return;
      syncSlotContent(slot);
    }, SYNC_SELECTION_DEBOUNCE_MS);
    slotUpdateDebouncers.set(slot, debouncedUpdate);
  }

  const alreadyObserved = slot.dataset.sizebaySlotObserved === 'true';

  if (!alreadyObserved) {
    slot.dataset.sizebaySlotObserved = 'true';
    observedSlots.add(slot);

    const observer = new MutationObserver(() => {
      if (suppressSlotObserverSync) return;
      debouncedUpdate();
    });

    observer.observe(slot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    slotObservers.set(slot, observer);
    syncSlotContent(slot, options);
    return;
  }

  // Already observing — only re-sync when the caller moved DOM into the slot.
  if (options.forceSync) {
    syncSlotContent(slot, options);
  }
};

/** @param {MutationRecord[]} mutations */
const isRecommendationContentOnlyMutation = (mutations) => {
  return mutations.every((mutation) => isSizebayRecommendationMutationTarget(mutation.target));
};

/** @param {MutationRecord[]} mutations */
const isIgnorablePickerMutation = (mutations) => {
  // Variant morph after Sizebay selection — skip layout sync until reveal settles.
  if (pendingRevealAfterVariantUpdate) {
    return mutations.every((mutation) => {
      const target = mutationTargetElement(mutation.target);
      if (!(target instanceof Element)) return false;
      if (isSizebayRecommendationMutationTarget(target)) return false;

      return target.closest('variant-picker') !== null || target.closest('.product-details') !== null;
    });
  }

  if (areAllSizeFieldsetsRevealed()) {
    return mutations.every((mutation) => {
      const target = mutationTargetElement(mutation.target);
      if (!(target instanceof Element)) return false;

      if (isSizebayRecommendationMutationTarget(target)) {
        return false;
      }

      return target.closest('variant-picker') !== null || target.closest('.product-details') !== null;
    });
  }

  if (!isSizePickerPending()) {
    return mutations.every((mutation) => {
      const target = mutationTargetElement(mutation.target);
      if (!(target instanceof Element)) return false;

      return (
        target.closest('.variant-option__grid-container') !== null ||
        target.closest('[data-legend-value]') !== null ||
        target.closest('.variant-option__legend-row') !== null ||
        target.closest('.variant-option__sizebay-slot') !== null
      );
    });
  }

  return mutations.every((mutation) => {
    const target = mutationTargetElement(mutation.target);
    if (!(target instanceof Element)) return false;

    return (
      target.closest('.variant-option__grid-container') !== null ||
      target.closest('[data-legend-value]') !== null ||
      target.closest('.variant-option__legend-row') !== null ||
      target.closest('.variant-option__sizebay-slot') !== null
    );
  });
};

/** @returns {HTMLElement | null} */
const getObserverRoot = () => {
  const root = document.querySelector(OBSERVER_ROOT_SELECTOR);
  return root instanceof HTMLElement ? root : null;
};

/** @param {string} size @param {HTMLElement | null | undefined} [fieldset] */
const findSizeInput = (size, fieldset = getSizeFieldset()) => {
  if (!(fieldset instanceof HTMLElement) || !size) return null;

  const escaped = CSS.escape(String(size));
  const byValue = fieldset.querySelector(`input[type="radio"][value="${escaped}"]`);
  if (byValue instanceof HTMLInputElement) return byValue;

  for (const label of fieldset.querySelectorAll('label.variant-option__grid-item')) {
    for (const span of label.querySelectorAll('[data-region-label]')) {
      if (span.textContent?.trim() === String(size)) {
        const input = label.querySelector('input[type="radio"]');
        if (input instanceof HTMLInputElement) return input;
      }
    }
  }

  return null;
};

/** @param {HTMLInputElement} input */
const isSizeAvailable = (input) => {
  if (input.dataset.optionAvailable === 'false') return false;
  if (input.getAttribute('aria-disabled') === 'true') return false;
  if (input.closest('label')?.classList.contains('is-unavailable')) return false;
  return true;
};

/** @param {HTMLElement} el */
const parseRecommendedSize = (el) => {
  const emphasized = el.querySelector('b, strong')?.textContent?.trim();
  if (emphasized) return emphasized;

  const match = el.textContent?.match(/recommend(?:ed)?\s+size\s+(\S+)/i);
  return match?.[1]?.trim() || null;
};

/** @param {HTMLInputElement} input */
const updateLegend = (input) => {
  const fieldset = input.closest('fieldset');
  if (!(fieldset instanceof HTMLFieldSetElement)) return;

  const legendValue = fieldset.querySelector('[data-legend-value]');
  if (!(legendValue instanceof HTMLElement)) return;

  const label = input.closest('label');
  const region = fieldset.dataset.currentRegion;
  let text = input.value;

  if (label instanceof HTMLElement) {
    const regionSpan = region != null && region !== '' ? label.querySelector(`[data-region-label="${region}"]`) : null;
    const visibleSpan = label.querySelector('[data-region-label]:not([hidden])');
    const span = regionSpan ?? visibleSpan;
    text = span?.textContent?.trim() ?? input.value;
  }

  legendValue.textContent = text;
};

/** @param {HTMLElement} fieldset @returns {boolean} Whether a variant update was triggered. */
const selectFirstAvailableSize = (fieldset) => {
  /** @type {HTMLInputElement | null} */
  let firstAvailable = null;

  for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
    if (!(input instanceof HTMLInputElement) || !isSizeAvailable(input)) continue;
    firstAvailable ??= input;
    break;
  }

  if (!(firstAvailable instanceof HTMLInputElement)) return false;

  if (firstAvailable.checked) {
    // Legend is filled on reveal while pending; avoid writing early.
    if (!isFieldsetPending(fieldset)) {
      updateLegend(firstAvailable);
    }
    return false;
  }

  pendingRevealAfterVariantUpdate = true;
  firstAvailable.click();
  return true;
};

/** @param {HTMLInputElement} input @param {HTMLElement} fieldset */
const triggerSizeInputSelection = (input, fieldset) => {
  if (isFieldsetPending(fieldset)) {
    pendingRevealAfterVariantUpdate = true;
  }

  isApplyingSizebaySelection = true;

  try {
    input.click();

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } finally {
    isApplyingSizebaySelection = false;
  }
};

/** @param {string} size @param {HTMLElement} fieldset */
const selectRecommendedSize = (size, fieldset) => {
  const input = findSizeInput(size, fieldset);
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  if (!input.checked) {
    triggerSizeInputSelection(input, fieldset);
    return;
  }

  // Do not updateLegend here while pending — revealSizePicker commits legend +
  // grid styles together after awaiting-selection clears.
  if (!isFieldsetPending(fieldset)) {
    updateLegend(input);
  }

  getDebouncedTryRevealAfterPresentation()();
};

/** @param {HTMLElement} el */
const syncSelection = (el) => {
  const fieldset = getRecommendationFieldset(el);
  if (!(fieldset instanceof HTMLElement)) return;

  const size = parseRecommendedSize(el);
  if (!size) return;

  cancelNoRecommendationReveal();

  const pending = isFieldsetPending(fieldset);
  const cachedSize = lastRecommendedSizeByFieldset.get(fieldset);

  if (!pending) {
    if (size === cachedSize) return;

    lastRecommendedSizeByFieldset.set(fieldset, size);

    const input = findSizeInput(size, fieldset);
    if (input instanceof HTMLInputElement) {
      selectRecommendedSize(size, fieldset);
    }

    return;
  }

  if (size === cachedSize) {
    if (!pendingRevealAfterVariantUpdate) {
      getDebouncedTryRevealAfterPresentation()();
    }
    return;
  }

  lastRecommendedSizeByFieldset.set(fieldset, size);
  selectRecommendedSize(size, fieldset);
};

/** @param {{ size?: string }} payload */
const handleAddToCart = ({ size }) => {
  if (!size) return;

  const input = findSizeInput(size);
  if (!(input instanceof HTMLInputElement)) return;

  if (!isSizeAvailable(input)) {
    const fieldset = input.closest('fieldset.variant-option--grid');
    if (fieldset instanceof HTMLElement) {
      selectRecommendedSize(size, fieldset);
    }
    return;
  }

  input.click();

  window.setTimeout(() => {
    const button = document.querySelector('add-to-cart-component button[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement && !button.disabled) {
      button.click();
    }
  }, ADD_TO_CART_DELAY_MS);
};

const patchImplantation = () => {
  const sizebay = /** @type {any} */ (window).Sizebay;
  if (!sizebay?.Implantation || sizebay.Implantation.__sizebayAddToCartPatched) {
    return Boolean(sizebay?.Implantation);
  }

  const original = sizebay.Implantation.bind(sizebay);

  /**
   * @param {unknown} payload
   * @param {{ onAddToCart?: (data: { size?: string }) => void } | undefined} events
   */
  sizebay.Implantation = (payload, events) => {
    if (events && typeof events === 'object') {
      events.onAddToCart = handleAddToCart;
    }
    return original(payload, events);
  };

  sizebay.Implantation.__sizebayAddToCartPatched = true;
  clearImplantationPatchTimeout();
  return true;
};

const clearImplantationPatchTimeout = () => {
  if (implantationPatchTimeoutId != null) {
    window.clearTimeout(implantationPatchTimeoutId);
    implantationPatchTimeoutId = null;
  }
};

const startImplantationPatch = () => {
  if (patchImplantation()) return;

  clearImplantationPatchTimeout();
  implantationPatchTimeoutId = window.setTimeout(() => {
    implantationPatchTimeoutId = null;
    patchImplantation();
  }, SIZEBAY_IMPLANTATION_PATCH_TIMEOUT_MS);
};

/**
 * @param {HTMLElement} slot
 * @param {HTMLElement} slotInner
 * @param {boolean} didChange
 */
const syncSlotAfterLayout = (slot, didChange) => {
  if (didChange) {
    suppressSlotObserverSync = true;
    observeSlot(slot, { forceSync: true, applySelection: true });
    queueMicrotask(() => {
      suppressSlotObserverSync = false;
    });
    return;
  }

  observeSlot(slot);
};

/**
 * Sizebay keeps `#szb-look-N` before the fieldset (required for Find my size).
 * Move `#szb-vfr-recommendation` into the reserved Liquid slot.
 */
const syncLayout = () => {
  if (isSyncingLayout) {
    pendingSyncLayoutRequest = true;
    return;
  }
  isSyncingLayout = true;

  try {
    for (const fieldset of getAllSizeFieldsets()) {
      const lookInside = fieldset.querySelector('[id^="szb-look-"]');
      if (lookInside instanceof HTMLElement) {
        fieldset.insertAdjacentElement('beforebegin', lookInside);
      }

      const look = fieldset.previousElementSibling;
      if (!(look instanceof HTMLElement) || !look.id?.startsWith('szb-look-')) continue;

      const slot = fieldset.querySelector('[data-sizebay-recommendation-slot]');
      if (!(slot instanceof HTMLElement)) continue;

      const slotInner = getSlotInner(slot);
      if (!slotInner) continue;

      for (const stray of fieldset.querySelectorAll(':scope > #szb-vfr-recommendation')) {
        stray.remove();
      }

      const recommendationInLook = look.querySelector('#szb-vfr-recommendation');
      const recommendationInSlot = slot.querySelector('#szb-vfr-recommendation');

      if (
        recommendationInSlot instanceof HTMLElement &&
        recommendationInSlot === slotInner.querySelector('#szb-vfr-recommendation')
      ) {
        const lookHasUpdatedRecommendation =
          recommendationInLook instanceof HTMLElement &&
          (recommendationInLook !== recommendationInSlot ||
            recommendationInLook.textContent?.replace(/\u00a0/g, ' ').trim() !==
              recommendationInSlot.textContent?.replace(/\u00a0/g, ' ').trim());

        let didChange = false;

        if (lookHasUpdatedRecommendation) {
          if (recommendationInLook !== recommendationInSlot) {
            recommendationInSlot.remove();
          }

          if (!slotInner.contains(recommendationInLook)) {
            slotInner.appendChild(recommendationInLook);
            didChange = true;
          } else if (recommendationInLook === recommendationInSlot) {
            didChange = true;
          }
        }

        syncSlotAfterLayout(slot, didChange);
        continue;
      }

      const inSlot = Array.from(slot.querySelectorAll('#szb-vfr-recommendation'));

      if (recommendationInLook instanceof HTMLElement) {
        let didChange = false;

        for (const stale of inSlot) {
          if (stale !== recommendationInLook) {
            stale.remove();
            didChange = true;
          }
        }

        if (!slotInner.contains(recommendationInLook)) {
          slotInner.appendChild(recommendationInLook);
          didChange = true;
        }

        syncSlotAfterLayout(slot, didChange || inSlot.length === 0);
        continue;
      }

      if (inSlot.length === 0) {
        if (slot.classList.contains('is-populated')) {
          getDebouncedClearSlot(slot)(slot);
        }

        continue;
      }

      cancelPendingClearSlot(slot);

      const primary = inSlot[0];
      if (!(primary instanceof HTMLElement)) continue;

      let didChange = false;

      for (const duplicate of inSlot.slice(1)) {
        duplicate.remove();
        didChange = true;
      }

      if (!slotInner.contains(primary)) {
        slotInner.appendChild(primary);
        didChange = true;
      }

      syncSlotAfterLayout(slot, didChange);
    }
  } finally {
    requestAnimationFrame(() => {
      isSyncingLayout = false;

      if (ensureSizebaySyncReady()) {
        getDebouncedTryRevealAfterPresentation()();

        if (hasPendingRecommendationInAnyLook()) {
          cancelNoRecommendationReveal();
        } else if (hasPendingFieldsetsWaitingForRecommendation()) {
          scheduleNoRecommendationReveal();
        } else {
          cancelNoRecommendationReveal();
        }
      }

      if (pendingSyncLayoutRequest) {
        pendingSyncLayoutRequest = false;
        syncLayout();
      }
    });
  }
};

const getSyncLayoutDebounceMs = () =>
  hasCompletedInitialSizebaySync && !isSizePickerPending() ? SYNC_LAYOUT_DEBOUNCE_MS : SYNC_LAYOUT_BOOT_DEBOUNCE_MS;

const scheduleSyncLayout = () => {
  if (syncLayoutTimeoutId != null) {
    window.clearTimeout(syncLayoutTimeoutId);
  }

  syncLayoutTimeoutId = window.setTimeout(() => {
    syncLayoutTimeoutId = null;
    syncLayout();
  }, getSyncLayoutDebounceMs());
};

scheduleSyncLayout.cancel = () => {
  if (syncLayoutTimeoutId != null) {
    window.clearTimeout(syncLayoutTimeoutId);
    syncLayoutTimeoutId = null;
  }
};

const disconnectLayoutObserver = () => {
  layoutObserver?.disconnect();
  layoutObserver = null;
};

const connectLayoutObserver = () => {
  disconnectLayoutObserver();

  const root = getObserverRoot();
  if (!root) return;

  layoutObserver = new MutationObserver((mutations) => {
    if (isRecommendationContentOnlyMutation(mutations)) {
      const allInSlots = mutations.every((mutation) => {
        const target = mutationTargetElement(mutation.target);
        return target instanceof Element && target.closest('.variant-option__sizebay-slot') !== null;
      });

      // Slot observers already handle in-slot recommendation updates.
      if (allInSlots) return;

      scheduleSyncLayout.cancel();
      syncLayout();
      return;
    }

    if (isIgnorablePickerMutation(mutations)) return;
    scheduleSyncLayout();
  });

  layoutObserver.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  });
};

const init = () => {
  if (!document.querySelector('#sizebay-container')) return;

  refreshFieldsetCache();
  bootstrapSizebayIfReady();
  bindUserSizeSelectionListener();
  scheduleRevealPickerFallback();
  startImplantationPatch();

  const handleSizebayLoaded = () => {
    isSizebayLoaded = true;
    patchImplantation();
    scheduleSyncLayout.cancel();
    cancelNoRecommendationReveal();
    syncLayout();
  };

  window.addEventListener('sizebay:loaded', handleSizebayLoaded);

  scheduleSyncLayout.cancel();
  connectLayoutObserver();

  // One initial sync. If Sizebay is already booted, reuse the loaded handler;
  // otherwise wait for sizebay:loaded (or MutationObserver) instead of syncing twice.
  if (isSizebayRuntimeReady()) {
    handleSizebayLoaded();
  } else {
    syncLayout();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!(section instanceof HTMLElement)) return;

  if (!section.querySelector('#sizebay-container')) {
    if (section.querySelector('variant-picker') || section.querySelector('.product-details')) {
      teardownSizePickerIntegration();
    }
    return;
  }

  reinitializeSizePickerForSection();
});

document.addEventListener('variant:update', (event) => {
  if (event instanceof CustomEvent && event.detail?.data?.newProduct) {
    if (document.querySelector('#sizebay-container')) {
      reinitializeSizePickerForSection();
    }
    return;
  }

  if (!pendingRevealAfterVariantUpdate || !isSizePickerPending()) return;

  afterVariantPickerSettles(() => {
    pendingRevealAfterVariantUpdate = false;

    for (const fieldset of getSizeFieldsets()) {
      if (!isFieldsetPending(fieldset)) continue;

      const slot = fieldset.querySelector('[data-sizebay-recommendation-slot]');
      const recommendation = slot?.querySelector('#szb-vfr-recommendation');

      if (recommendation instanceof HTMLElement && slot instanceof HTMLElement) {
        syncRecommendationPresentation(recommendation, slot);
      }
    }

    tryRevealSizePicker();
    revealFieldsetsWithoutRecommendation();
  });
});
